import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Breadcrumbs,
  Link,
  Divider,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Home as HomeIcon,
  AccessTime as TimeIcon,
  Bookmark as BookmarkIcon,
  Share as ShareIcon,
  KeyboardArrowRight,
} from '@mui/icons-material';
import favicon from '../assets/favicon.png';

const GuidelineSection = ({ number, title, items }) => (
  <Box sx={{ mb: 5 }}>
    <Typography 
      variant="h5" 
      sx={{ 
        color: '#000',
        mb: 3,
        fontFamily: 'Poppins, sans-serif',
        fontWeight: 600,
        display: 'flex',
        gap: 2,
        borderBottom: '2px solid #f0f0f0',
        pb: 2,
      }}
    >
      {number}. {title}
    </Typography>
    <Box 
      component="ul" 
      sx={{ 
        listStyle: 'none', 
        pl: 0,
        m: 0,
      }}
    >
      {items.map((item, index) => (
        <Typography 
          key={index}
          component="li" 
          sx={{ 
            mb: 2,
            color: '#444',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 400,
            fontSize: '0.95rem',
            lineHeight: 1.7,
            pl: 3,
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: 0,
              top: '0.7em',
              width: '6px',
              height: '6px',
              backgroundColor: '#ffd700',
            }
          }}
        >
          {item}
        </Typography>
      ))}
    </Box>
  </Box>
);

const TableOfContents = ({ sections }) => (
  <Box
    sx={{
      position: 'sticky',
      top: 24,
      width: '300px',
      ml: 4,
      backgroundColor: '#f8f8f8',
      borderLeft: '3px solid #ffd700',
      display: { xs: 'none', lg: 'block' },
      height: 'fit-content',
    }}
  >
    <Typography
      variant="h6"
      sx={{
        p: 3,
        pb: 2,
        fontFamily: 'Poppins, sans-serif',
        fontWeight: 600,
        fontSize: '1.1rem',
        borderBottom: '1px solid #eee',
        color: '#222',
      }}
    >
      Table of Contents
    </Typography>
    <Box 
      component="nav"
      sx={{
        p: 3,
        pt: 2,
      }}
    >
      {sections.map((section, index) => (
        <Link
          key={index}
          href={`#section-${index + 1}`}
          sx={{
            display: 'block',
            mb: 2,
            color: '#555',
            textDecoration: 'none',
            fontSize: '0.95rem',
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 400,
            transition: 'all 0.2s ease',
            pl: 2,
            borderLeft: '2px solid transparent',
            '&:hover': {
              color: '#000',
              borderLeft: '2px solid #ffd700',
              backgroundColor: '#fff',
            },
          }}
        >
          {index + 1}. {section.title}
        </Link>
      ))}
    </Box>
  </Box>
);

const Guidelines = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const sections = [
    {
      title: "General Guidelines",
      items: [
        "Use Larrvaris responsibly and ethically.",
        "Respect all users and engage in constructive communication.",
        "Ensure that any content you submit complies with our policies."
      ]
    },
    {
      title: "Content Guidelines",
      items: [
        "Do not post or share illegal, harmful, or offensive content.",
        "Respect copyright laws—do not upload content you do not own.",
        "Avoid spamming, misleading, or deceptive information."
      ]
    },
    {
      title: "User Conduct",
      items: [
        "Do not impersonate others or create multiple fake accounts.",
        "Do not engage in harassment, hate speech, or discrimination.",
        "Do not attempt to exploit or hack the website's systems."
      ]
    },
    {
      title: "Privacy & Security",
      items: [
        "Keep your account information secure.",
        "Do not share personal data of others without consent.",
        "Report any suspicious activity or security concerns immediately."
      ]
    },
    {
      title: "Prohibited Activities",
      items: [
        "No fraudulent transactions or misrepresentation.",
        "No unauthorized advertising or solicitation.",
        "No distribution of malware, phishing, or hacking attempts."
      ]
    },
    {
      title: "Compliance & Enforcement",
      items: [
        "Violations of these guidelines may result in warnings, account suspension, or termination.",
        "Larrvaris reserves the right to remove content that does not meet these guidelines.",
        "Users can report violations through our contact channels."
      ]
    },
    {
      title: "Updates & Changes",
      items: [
        "These guidelines may be updated periodically.",
        "Users are encouraged to review the guidelines regularly to stay informed."
      ]
    }
  ];

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Black Header Banner */}
      <Box 
        sx={{ 
          backgroundColor: '#000',
          color: '#ffd700',
          py: 6,
          borderRadius: 0,
        }}
      >
        <Container maxWidth="lg">
          {/* Updated Navigation Style */}
          <Box 
            sx={{ 
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 4,
              fontFamily: '"PT Sans Narrow", sans-serif',
            }}
          >
            <Link
              href="/"
              sx={{
                color: '#ffd700',
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                fontSize: '0.9rem',
                fontWeight: 700,
                letterSpacing: '0.05em',
                fontFamily: '"PT Sans Narrow", sans-serif',
                '&:hover': {
                  color: '#fff',
                },
              }}
            >
              ➜ Home
            </Link>
            <KeyboardArrowRight sx={{ color: '#ffd700', fontSize: '1.2rem' }} />
            <Typography 
              sx={{ 
                color: '#ffd700',
                fontSize: '0.9rem',
                fontWeight: 700,
                letterSpacing: '0.05em',
                fontFamily: '"PT Sans Narrow", sans-serif',
              }}
            >
              Guidelines
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="h2" 
                sx={{ 
                  fontWeight: 700,
                  mb: 2,
                  fontFamily: 'Poppins, sans-serif',
                  letterSpacing: '-0.02em',
                }}
              >
                Community Guidelines
              </Typography>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  opacity: 0.9,
                  fontFamily: 'Poppins, sans-serif',
                  letterSpacing: '0.02em',
                  fontWeight: 300,
                }}
              >
                Essential rules and practices for a productive community
              </Typography>
            </Box>
            
            <Box 
              component="img"
              src={favicon}
              alt="Larrvaris Logo"
              sx={{
                width: '100px',
                height: '100px',
                filter: 'brightness(0) invert(1)',
                opacity: 0.9,
              }}
            />
          </Box>
        </Container>
      </Box>

      {/* Content Section with Table of Contents */}
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'space-between',
          gap: 4,
        }}>
          {/* Main Content */}
          <Box sx={{ 
            backgroundColor: '#fff',
            p: { xs: 3, md: 5 },
            flex: 1,
            maxWidth: '800px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          }}>
            {/* Welcome Text */}
            <Typography 
              variant="body1" 
              sx={{ 
                mb: 5,
                color: '#444',
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 400,
                lineHeight: 1.7,
                fontSize: '1rem',
                pb: 4,
                borderBottom: '1px solid #eee',
              }}
            >
              Welcome to Larrvaris! These guidelines are designed to ensure our platform remains a safe, 
              productive, and welcoming environment for all users. Please take time to familiarize yourself 
              with these important principles that govern our community.
            </Typography>

            {/* Guidelines Sections */}
            {sections.map((section, index) => (
              <Box 
                key={index}
                id={`section-${index + 1}`}
                sx={{ 
                  scrollMarginTop: '2rem',
                }}
              >
                <GuidelineSection 
                  number={index + 1}
                  title={section.title}
                  items={section.items}
                />
              </Box>
            ))}
          </Box>

          {/* Table of Contents */}
          <TableOfContents sections={sections} />
        </Box>
      </Container>
    </Box>
  );
};

export default Guidelines; 